import { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import LocationSearch from '../components/LocationSearch';
import MapPicker from '../components/MapPicker';
import { Truck, Calculator, AlertCircle, CheckCircle, CreditCard, Clock } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useTenant } from '../context/TenantContext';
import { usePreventBack } from '../hooks/usePreventBack';
import Loading from '../components/Loading';

export default function Checkout() {
    usePreventBack(true);
    const { cartItems, cartTotal } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [calculatingDelivery, setCalculatingDelivery] = useState(false);
    const [deliveryError, setDeliveryError] = useState('');
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [modalErrorMsg, setModalErrorMsg] = useState('');
    const hasStockErrors = cartItems?.some(item => parseInt(item.quantity) > parseInt(item.stock_quantity));
    const toast = useToast();
    const { tenant } = useTenant();
    const [formData, setFormData] = useState({
        phone: '',
        notes: ''
    });
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [restaurantLocation, setRestaurantLocation] = useState(null);
    const [deliveryDetails, setDeliveryDetails] = useState(null);
    const [showMap, setShowMap] = useState(false);

    const targetRestaurantId = tenant?.id || (cartItems && cartItems.length > 0 ? cartItems[0].restaurant_id : null);

    const [couponInput, setCouponInput] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponError, setCouponError] = useState('');

    const discountAmount = appliedCoupon ? appliedCoupon.discountAmount : 0;
    const displaySubtotal = cartTotal;
    const gstPercentage = deliveryDetails?.gst_percentage || 5;
    const displayDeliveryFee = deliveryDetails?.deliveryFee ?? null;
    const displayGst = displayDeliveryFee !== null 
        ? Math.round((displaySubtotal - discountAmount) * (gstPercentage / 100) * 100) / 100 
        : null;
    const displayTotal = displayDeliveryFee !== null 
        ? Math.max(0, (displaySubtotal - discountAmount + displayDeliveryFee + displayGst)) 
        : null;

    const handleApplyCoupon = async () => {
        if (!couponInput.trim()) return;
        setCouponError('');
        try {
            const response = await api.post(`/api/coupons/apply?restaurantId=${targetRestaurantId}`, {
                code: couponInput.trim(),
                items: cartItems.map(item => ({
                    id: item.menu_item_id || item.id,
                    price: item.price,
                    quantity: item.quantity,
                    category_id: item.category_id
                }))
            });

            if (response.data.success) {
                setAppliedCoupon(response.data);
                toast.success(`Coupon ${response.data.code} applied successfully!`);
            }
        } catch (error) {
            console.error('Error applying coupon:', error);
            setCouponError(error.response?.data?.message || 'Failed to apply coupon');
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponInput('');
        setCouponError('');
    };

    useEffect(() => {
        if (targetRestaurantId) {
            fetchRestaurantLocation();
        }
    }, [targetRestaurantId]);

    useEffect(() => {
        if (user && user.phone) {
            setFormData(prev => ({ ...prev, phone: user.phone }));
        }
    }, [user?.phone]);

    const fetchRestaurantLocation = async () => {
        if (!targetRestaurantId) return;
        try {
            const response = await api.get(`/api/delivery/restaurant-location?restaurantId=${targetRestaurantId}`);
            setRestaurantLocation(response.data);
        } catch (error) {
            console.error('Error fetching restaurant location:', error);
        }
    };

    const handleLocationSelect = (location) => {
        setSelectedLocation(location);
        setDeliveryDetails(null); // Reset delivery calculation when location changes
        setDeliveryError('');

        // Auto-calculate delivery when location is selected
        if (location) {
            calculateDelivery(location);
        }
    };

    const calculateDelivery = async (location) => {
        if (!location) return;

        setDeliveryError('');
        setCalculatingDelivery(true);

        try {
            const postcodeMatch = location.address ? location.address.match(/\b\d{6}\b/) : null;
            const deliveryPostcode = postcodeMatch ? postcodeMatch[0] : '';

            const response = await api.post('/api/delivery/calculate', {
                latitude: location.lat,
                longitude: location.lng,
                deliveryPostcode: deliveryPostcode,
                subtotal: cartTotal,
                restaurantId: targetRestaurantId
            });

            if (response.data.success) {
                setDeliveryDetails(response.data);
                console.log('Delivery calculated:', response.data);
            } else {
                setDeliveryError('Failed to calculate delivery. Please try again.');
            }
        } catch (error) {
            console.error('Error calculating delivery:', error);
            const errorMsg = error.response?.data?.message || 'Failed to calculate delivery. Please check your connection.';
            setDeliveryError(errorMsg);
            setModalErrorMsg(errorMsg);
            setShowErrorModal(true);
        } finally {
            setCalculatingDelivery(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!cartItems || cartItems.length === 0) {
            toast.warning('Your cart is empty!');
            navigate('/menu');
            return;
        }

        if (!user?.id) {
            toast.warning('Please login again');
            navigate('/login');
            return;
        }

        if (hasStockErrors) {
            toast.error('One or more items in your cart exceed available stock. Please adjust your cart.');
            navigate('/cart');
            return;
        }

        if (!selectedLocation) {
            toast.warning('Please select a delivery location');
            return;
        }

        if (!deliveryDetails) {
            toast.info('Please wait for delivery calculation');
            return;
        }

        setLoading(true);

        try {
            // Create payment order (supports Cashfree or Razorpay dynamically)
            const payResponse = await api.post('/api/payments/create-order', {
                orderAmount: displayTotal,
                restaurantId: tenant?.id,
                customerDetails: {
                    customerId: String(user.id),
                    customerName: user.full_name || 'Customer',
                    customerEmail: user.email || 'customer@example.com',
                    customerPhone: formData.phone.replace(/\D/g, '').substring(0, 10)
                },
                orderMeta: {
                    returnUrl: `${window.location.origin}/payment-status`,
                    notifyUrl: `${import.meta.env.VITE_API_URL}/api/payments/webhook`
                }
            });

            const provider = payResponse.data.provider || 'cashfree';

            if (provider === 'razorpay') {
                const rzpOrderId = payResponse.data.orderId;
                const keyId = payResponse.data.keyId;

                // Create database order with /api/ prefix
                const orderResponse = await api.post('/api/orders', {
                    deliveryAddress: selectedLocation.address,
                    deliveryNotes: formData.notes,
                    contactPhone: formData.phone,
                    paymentSessionId: rzpOrderId,
                    aggregatorOrderId: rzpOrderId,
                    deliveryLatitude: selectedLocation.lat,
                    deliveryLongitude: selectedLocation.lng,
                    deliveryDistance: deliveryDetails.distance,
                    deliveryFee: deliveryDetails.deliveryFee,
                    taxAmount: displayGst,
                    subtotalAmount: deliveryDetails.subtotal,
                    finalTotalAmount: displayTotal,
                    couponCode: appliedCoupon ? appliedCoupon.code : null,
                    wa_ref: sessionStorage.getItem('wa_ref') || undefined
                });

                const dbOrderId = orderResponse.data.order.id;

                // Load Razorpay SDK
                const rzpScript = document.createElement('script');
                rzpScript.src = 'https://checkout.razorpay.com/v1/checkout.js';
                rzpScript.async = true;
                document.body.appendChild(rzpScript);

                rzpScript.onload = () => {
                    const options = {
                        key: keyId,
                        amount: payResponse.data.amount,
                        currency: payResponse.data.currency || 'INR',
                        name: tenant?.name || 'Savory Bites',
                        description: `Order #${dbOrderId}`,
                        order_id: rzpOrderId,
                        handler: function (response) {
                            const params = new URLSearchParams({
                                order_id: dbOrderId,
                                razorpay_payment_id: response.razorpay_payment_id || '',
                                razorpay_order_id: response.razorpay_order_id || '',
                                razorpay_signature: response.razorpay_signature || '',
                                status: 'verifying'
                            });
                            window.location.href = `/payment-status?${params.toString()}`;
                        },
                        prefill: {
                            name: user.full_name || 'Customer',
                            email: user.email || 'customer@example.com',
                            contact: formData.phone.replace(/\D/g, '').substring(0, 10)
                        },
                        theme: { color: tenant?.primary_color || '#5b4310' },
                        modal: {
                            ondismiss: function () {
                                setLoading(false);
                            }
                        }
                    };
                    const rzp = new window.Razorpay(options);
                    rzp.open();
                };
            } else {
                // Cashfree SDK Checkout Flow
                const paymentSessionId = payResponse.data.paymentSessionId;
                const cfOrderId = payResponse.data.orderId;

                // Create database order with /api/ prefix
                const orderResponse = await api.post('/api/orders', {
                    deliveryAddress: selectedLocation.address,
                    deliveryNotes: formData.notes,
                    contactPhone: formData.phone,
                    paymentSessionId: cfOrderId,
                    aggregatorOrderId: payResponse.data.cfOrderId,
                    deliveryLatitude: selectedLocation.lat,
                    deliveryLongitude: selectedLocation.lng,
                    deliveryDistance: deliveryDetails.distance,
                    deliveryFee: deliveryDetails.deliveryFee,
                    taxAmount: displayGst,
                    subtotalAmount: deliveryDetails.subtotal,
                    finalTotalAmount: displayTotal,
                    couponCode: appliedCoupon ? appliedCoupon.code : null,
                    wa_ref: sessionStorage.getItem('wa_ref') || undefined
                });

                const dbOrderId = orderResponse.data.order.id;

                // Load Cashfree SDK
                const cashfreeScript = document.createElement('script');
                cashfreeScript.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
                cashfreeScript.async = true;
                document.body.appendChild(cashfreeScript);

                cashfreeScript.onload = () => {
                    const cashfree = window.Cashfree({
                        mode: import.meta.env.VITE_CASHFREE_ENVIRONMENT === 'production' ? 'production' : 'sandbox'
                    });

                    cashfree.checkout({
                        paymentSessionId: paymentSessionId,
                        redirectTarget: '_self',
                        returnUrl: `${window.location.origin}/payment-status?order_id=${dbOrderId}&cf_order_id=${cfOrderId}`
                    });
                };
            }

        } catch (error) {
            console.error('Checkout Error:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Checkout failed';
            toast.error('Error: ' + errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const isDeliveryCalculated = !!deliveryDetails;

    if (!cartItems || cartItems.length === 0) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-16 text-center">
                <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
                <button onClick={() => navigate('/menu')} className="btn-primary">
                    Browse Menu
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            {loading && <Loading mode="full" message="Confirming your order..." />}
            <h1 className="text-3xl font-bold mb-8">Checkout</h1>

            <div className="grid lg:grid-cols-2 gap-8">
                {/* Left Column - Delivery Details */}
                <div className="space-y-6">
                    {/* Location Search */}
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h2 className="text-lg font-semibold mb-4 flex items-center">
                            <Truck className="w-5 h-5 mr-2 text-primary-600" />
                            Select Delivery Location
                        </h2>

                        <LocationSearch
                            onLocationSelect={handleLocationSelect}
                            selectedLocation={selectedLocation}
                        />

                        {/* Map Toggle */}
                        {selectedLocation && (
                            <div className="mt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowMap(!showMap)}
                                    className="text-sm text-primary-600 hover:underline"
                                >
                                    {showMap ? 'Hide map' : 'Adjust location on map'}
                                </button>

                                {showMap && (
                                    <div className="mt-2">
                                        <MapPicker
                                            onLocationSelect={handleLocationSelect}
                                            selectedLocation={selectedLocation}
                                            restaurantLocation={restaurantLocation}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Phone Number */}
                        <div className="mt-4">
                            <label className="block text-sm font-medium mb-1">Contact Phone *</label>
                            <input
                                type="tel"
                                required
                                pattern="[0-9]{10}"
                                value={formData.phone}
                                onChange={(e) => {
                                    setFormData({ ...formData, phone: e.target.value });
                                }}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                placeholder="10 digit mobile number"
                            />
                        </div>

                        {/* Delivery Notes */}
                        <div className="mt-4">
                            <label className="block text-sm font-medium mb-1">Delivery Notes (Optional)</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                rows="2"
                                placeholder="Ring doorbell, leave at gate, etc."
                            />
                        </div>

                        {/* Error Message */}
                        {deliveryError && (
                            <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-start">
                                <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                                {deliveryError}
                            </div>
                        )}

                        {/* Delivery Details */}
                        {isDeliveryCalculated && (
                            <div className="mt-4 bg-green-50 border border-green-200 p-4 rounded-lg">
                                <h3 className="font-medium text-green-900 mb-2 flex items-center">
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Delivery Calculated
                                </h3>
                                <div className="text-sm text-green-800 space-y-2">
                                    <div className="flex justify-between items-center bg-white/50 p-2 rounded border border-green-100">
                                        <span className="font-medium flex items-center">
                                            <Clock className="w-4 h-4 mr-2 text-primary-600" />
                                            Expected Delivery:
                                        </span>
                                        <span className="text-lg font-bold text-primary-700">
                                            {20 + Math.round(deliveryDetails.distance * 5)} mins
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 mt-2">
                                        <p>Distance: <span className="font-medium">{deliveryDetails.distance} km</span></p>
                                        <p>Fee: <span className="font-medium">₹{deliveryDetails.deliveryFee.toFixed(2)}</span></p>
                                        <p>GST ({deliveryDetails.gst_percentage}%): <span className="font-medium">₹{deliveryDetails.gstAmount.toFixed(2)}</span></p>
                                    </div>
                                    {deliveryDetails?.adloggs?.available && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                                                🏍️ Adloggs Delivery {deliveryDetails.adloggs.etd && `• Est. ${deliveryDetails.adloggs.etd}`}
                                            </span>
                                        </div>
                                    )}
                                    {deliveryDetails?.deliveryMode === 'partner' && deliveryDetails?.adloggs?.available && (
                                        <p className="text-[10px] text-indigo-600 mt-1">Delivered via Adloggs Delivery</p>
                                    )}
                                    {deliveryDetails.freeDeliveryEligible && (
                                        <p className="text-green-600 font-bold flex items-center bg-green-100 p-2 rounded mt-2">
                                            <span className="mr-2">✨</span> FREE delivery applied!
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Recalculate Button */}
                        {selectedLocation && !calculatingDelivery && (
                            <button
                                type="button"
                                onClick={() => calculateDelivery(selectedLocation)}
                                className="w-full mt-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center"
                            >
                                <Calculator className="w-4 h-4 mr-2" />
                                Recalculate Delivery
                            </button>
                        )}

                        {calculatingDelivery && (
                            <div className="mt-4">
                                <Loading mode="inline" message="Calculating delivery..." />
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column - Order Summary */}
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-lg shadow">
                        <h2 className="text-xl font-semibold mb-4 flex items-center">
                            <CreditCard className="w-5 h-5 mr-2" />
                            Order Summary
                        </h2>

                        {/* Items */}
                        <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                            {cartItems.map(item => {
                                const itemTotal = (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1);
                                return (
                                    <div key={item.id} className="flex justify-between text-sm">
                                        <span className={item.quantity > item.stock_quantity ? 'text-red-600 font-bold' : ''}>
                                            {item.name} × {item.quantity} {item.quantity > item.stock_quantity ? '(Out of Stock)' : ''}
                                        </span>
                                        <span className={item.quantity > item.stock_quantity ? 'text-red-600 line-through' : ''}>
                                            ₹{itemTotal.toFixed(2)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Coupon Code Input */}
                        <div className="border-t pt-4 mt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Apply Coupon</h3>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Enter coupon code"
                                    value={couponInput}
                                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                                    disabled={loading || appliedCoupon}
                                    className="flex-1 px-3 py-1.5 border rounded-lg uppercase text-sm focus:ring-2 focus:ring-primary-500 disabled:bg-gray-150"
                                />
                                {appliedCoupon ? (
                                    <button
                                        type="button"
                                        onClick={handleRemoveCoupon}
                                        className="px-3 py-1.5 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200"
                                    >
                                        Remove
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleApplyCoupon}
                                        disabled={loading || !couponInput.trim()}
                                        className="px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:bg-gray-205 disabled:text-gray-400"
                                    >
                                        Apply
                                    </button>
                                )}
                            </div>
                            {couponError && (
                                <p className="text-xs text-red-600 mt-1">{couponError}</p>
                            )}
                            {appliedCoupon && (
                                <p className="text-xs text-green-600 mt-1 font-medium">
                                    Code "{appliedCoupon.code}" applied! You saved ₹{appliedCoupon.discountAmount.toFixed(2)}
                                </p>
                            )}
                        </div>

                        {/* Pricing Breakdown */}
                        <div className="border-t space-y-2 pt-4">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Subtotal</span>
                                <span>₹{displaySubtotal.toFixed(2)}</span>
                            </div>

                            {appliedCoupon && (
                                <div className="flex justify-between text-green-600 font-medium">
                                    <span>Discount ({appliedCoupon.code})</span>
                                    <span>-₹{discountAmount.toFixed(2)}</span>
                                </div>
                            )}

                            <div className="flex justify-between">
                                <span className="text-gray-600 flex flex-col">
                                    <span>Delivery Fee</span>
                                    {deliveryDetails?.adloggs?.available && (
                                        <span className="text-[10px] text-indigo-600 font-semibold flex items-center gap-1 mt-0.5">
                                            🏍️ Adloggs Delivery {deliveryDetails.adloggs.etd ? `(${deliveryDetails.adloggs.etd})` : ''}
                                        </span>
                                    )}
                                </span>
                                <span>
                                    {displayDeliveryFee !== null ? (
                                        displayDeliveryFee === 0 ? (
                                            <span className="text-green-600 font-medium">FREE</span>
                                        ) : (
                                            `₹${displayDeliveryFee.toFixed(2)}`
                                        )
                                    ) : (
                                        <span className="text-orange-500 text-sm">
                                            Select location
                                        </span>
                                    )}
                                </span>
                            </div>

                            <div className="flex justify-between">
                                <span className="text-gray-600">GST ({deliveryDetails?.gst_percentage || 5}%)</span>
                                <span>
                                    {displayGst !== null ? (
                                        `₹${displayGst.toFixed(2)}`
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </span>
                            </div>

                            <div className="border-t pt-2 flex justify-between font-bold text-lg">
                                <span>Total</span>
                                <span>
                                    {displayTotal !== null ? (
                                        `₹${displayTotal.toFixed(2)}`
                                    ) : (
                                        <span className="text-orange-500 text-sm font-normal">
                                            Select location first
                                        </span>
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Pay Button */}
                        <button
                            onClick={handleSubmit}
                            disabled={loading || !isDeliveryCalculated || hasStockErrors}
                            className={`w-full py-3 text-lg mt-6 rounded-lg font-medium flex items-center justify-center ${(isDeliveryCalculated && !hasStockErrors)
                                ? 'btn-primary'
                                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            {loading ? (
                                <Loading mode="button" message="Processing..." />
                            ) : hasStockErrors ? (
                                'Fix Cart Stock Issues'
                            ) : !selectedLocation ? (
                                'Select Delivery Location'
                            ) : !isDeliveryCalculated ? (
                                <Loading mode="button" message="Calculating..." />
                            ) : (
                                <>
                                    <CreditCard className="w-5 h-5 mr-2" />
                                    Pay ₹{displayTotal.toFixed(2)}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            {/* Error Overlay Modal */}
            {showErrorModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-100 text-center space-y-6 transform scale-100 transition-all duration-300 animate-in zoom-in-95 duration-200">
                        <div className="mx-auto w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center animate-bounce">
                            <AlertCircle className="w-8 h-8" />
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-xl font-black text-gray-900">Service Limit Exceeded</h3>
                            <p className="text-sm text-gray-500 leading-relaxed">{modalErrorMsg}</p>
                        </div>
                        <button
                            onClick={() => {
                                setShowErrorModal(false);
                                setModalErrorMsg('');
                            }}
                            className="w-full py-3 bg-gray-900 text-white text-sm font-bold rounded-xl hover:bg-gray-800 transition-colors shadow-lg shadow-gray-250 outline-none"
                        >
                            Understood, Adjust Location
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}